import { useCallback, useEffect, useState } from "react";

import { readHrSettings, updateHrSettings } from "../api/hr";
import { describeNumberError, NumberField } from "../ui/number-field";
import type { NumberFieldRules } from "../ui/number-field";
import { PaneHead } from "./pane";
import styles from "./settings.module.css";
import { Toast } from "./toast";
import { describe, useWriter } from "./use-writer";

/** The window the column allows (`clinics.hr_warn_days`, CHECK 1–365). */
const WINDOW: NumberFieldRules = { min: 1, max: 365, integer: true, unit: "days" };

/**
 * **How early HR flags something** (HR-D7, STORY-181f).
 *
 * One number for the clinic: a certificate running out, or a probation
 * ending, within this many days is flagged on Team and Compliance and noticed
 * on the person's own Me tab. **In the app only** — nothing is emailed or sent
 * on WhatsApp (HR-D7), so this changes what is shown and nothing else.
 *
 * The number is the clinic's own: it opens on what is stored, never a default
 * of this screen's (*data is configured, not coded*).
 */
export function HrWarningsPane(): React.JSX.Element {
  const { busy, toast, dismiss, fail, run } = useWriter();
  const [stored, setStored] = useState<number | null>(null);
  const [days, setDays] = useState("");
  const error = describeNumberError(days, WINDOW);

  const reload = useCallback(async (signal?: AbortSignal) => {
    const settings = await readHrSettings(signal);
    setStored(settings.warnDays);
    setDays(String(settings.warnDays));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      fail(describe(cause, "Could not read the warning window."));
    });
    return () => controller.abort();
  }, [reload, fail]);

  const changed = stored !== null && days.trim() !== "" && Number(days) !== stored;

  return (
    <>
      <PaneHead title="Expiry warnings" />

      {stored === null ? null : (
        <section className={styles.card} data-testid="hr-warnings-pane">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>When HR starts warning</h2>
          </div>

          <div className={styles.cardBody}>
            <p className={styles.lead}>
              A certificate that runs out, or a probation that ends, within this many days is
              flagged on Staff &rarr; Team and Compliance, and noticed on the person&rsquo;s own Me
              tab. Nothing is emailed or messaged &mdash; it shows in DentaCore only.
            </p>

            <NumberField
              label="Warn this many days before"
              className={`${styles.field} ${styles.fieldShort}`}
              labelClassName={styles.label}
              inputClassName={styles.input}
              value={days}
              onChange={setDays}
              rules={WINDOW}
              disabled={busy}
              testId="hr-warn-days"
            />

            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.primary}
                disabled={busy || !changed || error !== null}
                onClick={() => {
                  const next = Number(days);
                  void run(
                    () => updateHrSettings(next),
                    () => reload(),
                    `HR now warns ${String(next)} ${next === 1 ? "day" : "days"} ahead.`,
                  );
                }}
                data-testid="hr-warn-save"
              >
                Save
              </button>
            </div>
          </div>
        </section>
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </>
  );
}
